import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Alert,
  LinearProgress,
  Chip,
} from '@mui/material';
import {
  CheckCircle,
  Schedule,
  Error,
  Refresh,
  Email,
  Upload,
} from '@mui/icons-material';

interface ProcessingStatusProps {
  status: {
    status: string;
    filename?: string;
    timestamp?: string;
    message?: string;
  } | null;
  onRetry: () => void;
  usageStats?: {
    report_count: number;
    limit: number;
    remaining: number;
    limit_reached: boolean;
  };
}

const ProcessingStatus: React.FC<ProcessingStatusProps> = ({ status, onRetry, usageStats }) => {
  const getStatusIcon = (statusValue: string) => {
    switch (statusValue) {
      case 'completed':
        return <CheckCircle sx={{ color: 'success.main', fontSize: 40 }} />;
      case 'processing':
        return <Schedule sx={{ color: 'info.main', fontSize: 40 }} />;
      case 'uploaded':
        return <Upload sx={{ color: 'primary.main', fontSize: 40 }} />;
      case 'failed':
        return <Error sx={{ color: 'error.main', fontSize: 40 }} />;
      default:
        return <Schedule sx={{ color: 'grey.500', fontSize: 40 }} />;
    }
  };

  const getStatusColor = (statusValue: string) => {
    switch (statusValue) {
      case 'completed':
        return 'success';
      case 'processing':
        return 'info';
      case 'uploaded':
        return 'primary';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusTitle = (statusValue: string) => {
    switch (statusValue) {
      case 'completed':
        return 'Analysis Completed! 🎉';
      case 'processing':
        return 'File Processing 🔄';
      case 'uploaded':
        return 'File Uploaded Successfully ✅';
      case 'failed':
        return 'Processing Failed ❌';
      default:
        return 'Ready to Upload 📤';
    }
  };

  const getStatusMessage = (statusValue: string, message?: string) => {
    if (message) return message;
    
    switch (statusValue) {
      case 'completed':
        return 'Analysis completed and email sent';
      case 'processing':
        return 'File processing';
      case 'uploaded':
        return 'File uploaded successfully, waiting in queue';
      case 'failed':
        return 'There was an error processing your medical report. Please try uploading again.';
      default:
        return 'Upload a medical report to get started with AI-powered analysis.';
    }
  };

  const formatTimestamp = (timestamp?: string) => {
    if (!timestamp) return '';
    
    try {
      const date = new Date(timestamp);
      return date.toLocaleString();
    } catch {
      return timestamp;
    }
  };

  if (!status || status.status === 'ready') {
    // If user has completed analyses, show a summary instead of "ready to upload"
    if (usageStats && usageStats.report_count > 0) {
      return (
        <Card>
          <CardContent>
            <Typography variant="h5" component="h2" gutterBottom>
              📊 Processing Status
            </Typography>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CheckCircle sx={{ color: 'success.main', fontSize: 60, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Analysis History
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                You have successfully analyzed <strong>{usageStats.report_count}</strong> medical report{usageStats.report_count !== 1 ? 's' : ''}.
                {usageStats.remaining > 0 && (
                  <> You have <strong>{usageStats.remaining}</strong> free analysis{usageStats.remaining !== 1 ? 'es' : ''} remaining.</>
                )}
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={onRetry}
                sx={{ px: 4 }}
              >
                📄 Submit New Report
              </Button>
            </Box>
          </CardContent>
        </Card>
      );
    }
    
    // Show ready to upload for new users
    return (
      <Card>
        <CardContent>
          <Typography variant="h5" component="h2" gutterBottom>
            📊 Processing Status
          </Typography>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Schedule sx={{ color: 'grey.500', fontSize: 60, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Ready to Upload
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Upload a medical report to get started with AI-powered analysis.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h5" component="h2" gutterBottom>
          📊 Processing Status
        </Typography>

        <Box sx={{ textAlign: 'center', mb: 3 }}>
          {getStatusIcon(status.status)}
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            {getStatusTitle(status.status)}
          </Typography>
          
          <Chip
            label={status.status.toUpperCase()}
            color={getStatusColor(status.status) as any}
            sx={{ mb: 2 }}
          />
        </Box>

        {/* Status Message */}
        <Alert 
          severity={
            status.status === 'completed' ? 'success' :
            status.status === 'failed' ? 'error' :
            status.status === 'processing' ? 'info' : 'info'
          }
          sx={{ mb: 3 }}
        >
          <Typography variant="body2">
            {getStatusMessage(status.status, status.message)}
          </Typography>
        </Alert>

        {/* File Information */}
        {status.filename && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              📄 File: {status.filename}
            </Typography>
            {status.timestamp && (
              <Typography variant="caption" color="textSecondary">
                🕒 {status.status === 'completed' ? 'Completed' : 'Submitted'}: {formatTimestamp(status.timestamp)}
              </Typography>
            )}
          </Box>
        )}

        {/* Processing Progress */}
        {status.status === 'processing' && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" gutterBottom>
              Processing your medical report...
            </Typography>
            <LinearProgress />
          </Box>
        )}

        {/* Action Buttons based on status */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
          {status.status === 'completed' && (
            <Button
              variant="outlined"
              startIcon={<Email />}
              disabled
            >
              Check Your Email
            </Button>
          )}

          {status.status === 'failed' && (
            <Button
              variant="contained"
              startIcon={<Refresh />}
              onClick={onRetry}
              color="primary"
            >
              Try Again
            </Button>
          )}

          {status.status === 'uploaded' && (
            <Alert severity="info" sx={{ flexGrow: 1 }}>
              <Typography variant="body2">
                <strong>💡 What happens next:</strong><br />
                • Your file is queued for AI analysis<br />
                • Processing typically takes 2-5 minutes<br />
                • You'll receive a detailed email with results<br />
                • You can close this page - processing continues in the background
              </Typography>
            </Alert>
          )}
        </Box>

        {/* Additional Information for Completed Status */}
        {status.status === 'completed' && (
          <Alert severity="success" sx={{ mt: 3 }}>
            <Typography variant="body2" gutterBottom>
              <strong>📋 What you received in your email:</strong>
            </Typography>
            <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
              <li>Detailed analysis of all medical parameters</li>
              <li>Identification of abnormal values with explanations</li>
              <li>Medical recommendations and next steps</li>
              <li>Patient-friendly explanations of complex medical terms</li>
            </ul>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default ProcessingStatus;




